// src/components/Pages/Home.js - Simplified with Dynamic Counts
import React, { useState, useEffect } from 'react';
import { 
  Target, Activity, Users, Heart, Globe, CheckCircle, TrendingUp, Star, User,
  Calendar, Clock, MapPin, ArrowRight, Image as ImageIcon, Eye,
  GraduationCap, Utensils, 
} from 'lucide-react';
import { SAHAYAA_VALUES, CORE_VALUES } from '../../utils/constants';
import { db } from '../../config/supabase';


const Home = ({ 
  setActiveSection, 
  setShowContactForm, 
  testimonials = [],
  events = []
}) => {
  const [dynamicStats, setDynamicStats] = useState({
    peopleHelped: 5000,
    eventsCompleted: 0,
    volunteers: 50,
    donors: 0,
    yearsOfService: 5
  });

  useEffect(() => {
    loadDynamicCounts();
  }, []);

  const loadDynamicCounts = async () => {
    try {
      // Load actual counts from database
      const [donationStats, eventStats,volunteerStats,orgStats] = await Promise.all([
        db.getDonationStats(),
        db.getEventStats(),
      db.getVolunteerStats(), 
      db.getOrganizationMemberStats(),
      ]);

      setDynamicStats(prev => ({
        ...prev,
        donors: donationStats.data?.uniqueDonors || 0,
        eventsCompleted: eventStats.data?.completedEvents || 0,
        volunteers: volunteerStats.data?.totalVolunteers || 0,
        members: orgStats.data?.totalOrgMembers || 0   

        // Keep other values as default for now
      }));
    } catch (error) {
      console.error('Error loading dynamic counts:', error);
    }
  };

  return (
    <div className="space-y-16">
      {/* Hero Section */}
      <HeroSection 
        setActiveSection={setActiveSection} 
        setShowContactForm={setShowContactForm} 
        stats={dynamicStats} 
      />
      
      {/* Simplified Donation Section */}
      <SimpleDonationSection setActiveSection={setActiveSection} />
      
      {/* Upcoming Events Section */}
      <UpcomingEventsSection events={events} setActiveSection={setActiveSection} />
      
      {/* Event Highlights Section */}
      <EventHighlightsSection events={events} setActiveSection={setActiveSection} />
      
      {/* Impact Dashboard */}
      {/* <ImpactDashboard /> */}
      
      {/* Donation Calculator */}
      {/* <DonationCalculator onDonate={(amount, cause) => {
        setActiveSection('donations');
      }} /> */}
      
      {/* Vision & Mission */}
      <VisionMission />
      
      {/* SAHAYAA Values */}
      <SahayaaValues />
      
      {/* Our Ideology */}
      <OurIdeology />
      
      {/* Core Values */}
      <CoreValues />
      
      {/* Testimonials */}
      {testimonials.length > 0 && <TestimonialsSection testimonials={testimonials} />}
    </div>
  );
};

// Simplified Donation Section (removed comparison stats)
const SimpleDonationSection = ({ setActiveSection }) => (
  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-10 rounded-3xl border border-green-200">
    <div className="text-center max-w-4xl mx-auto">
      <div className="flex items-center justify-center mb-6">
        <Heart className="w-10 h-10 text-green-600 mr-3" />
        <h2 className="text-4xl font-bold text-green-800">Support Our Mission</h2>
      </div>
      
      <p className="text-xl text-gray-700 mb-8 leading-relaxed">
        Your support helps us continue building a compassionate society where everyone lives with dignity, 
        health, knowledge, and joy. Every contribution makes a meaningful difference.
      </p>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <ImpactCard 
          icon={GraduationCap}
          title="Education Support"
          description="Help provide quality education and learning resources to children in need"
          color="blue"
        />
        <ImpactCard 
          icon={Activity}
          title="Healthcare Access"
          description="Support medical care and health awareness programs in communities"
          color="red"
        />
        <ImpactCard 
          icon={Utensils}
          title="Nutrition Programs"
          description="Fight hunger and malnutrition with nutritious meal programs"
          color="orange"
        />
      </div>

      <button
        onClick={() => setActiveSection('donations')}
        className="bg-green-600 text-white px-10 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg"
      >
        Make a Donation
      </button>
    </div>
  </div>
);

const ImpactCard = ({ icon: Icon, title, description, color }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-600',
    red: 'bg-red-50 border-red-200 text-red-600',
    orange: 'bg-orange-50 border-orange-200 text-orange-600'
  };

  return (
    <div className={`p-6 rounded-xl border ${colors[color].split(' ')[0]} ${colors[color].split(' ')[1]}`}>
      <Icon className={`w-10 h-10 mx-auto mb-4 ${colors[color].split(' ')[2]}`} />
      <h3 className="text-lg font-bold text-gray-800 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  );
};

const UpcomingEventsSection = ({ events, setActiveSection }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const upcomingEvents = events
    .filter(event => {
      const eventDate = new Date(event.date);
      const isUpcoming = eventDate >= today;
      const isNotCancelled = (event.event_status || '').toLowerCase() !== 'cancelled';
      return isUpcoming && isNotCancelled;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 3);

  return (
    <div className="bg-white p-10 rounded-3xl shadow-xl">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <Calendar className="w-8 h-8 text-green-600 mr-3" />
          <h2 className="text-4xl font-bold text-green-800">Upcoming Events</h2>
        </div>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Join us in our upcoming activities and be part of positive change in our community.
        </p>
      </div>
      
      {upcomingEvents.length > 0 ? (
        <>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {upcomingEvents.map((event) => (
              <UpcomingEventCard key={event.id} event={event} />
            ))}
          </div>
          
          <div className="text-center">
            <button
              onClick={() => setActiveSection('events')}
              className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 mx-auto"
            >
              <span>View All Events</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-2xl">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No Upcoming Events</h3>
          <p className="text-gray-500">
            We're planning exciting events. Check back soon or contact us to learn about upcoming activities!
          </p>
          <button
            onClick={() => setActiveSection('events')}
            className="mt-4 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            View All Events
          </button>
        </div>
      )}
    </div>
  );
};

const EventHighlightsSection = ({ events, setActiveSection }) => {
  const highlightEvents = events
    .filter(event => {
      const isFeatured = event.is_featured === true;
      const hasGallery = event.gallery_images && event.gallery_images.length > 0;
      const isCompleted = (event.event_status || '').toLowerCase() === 'completed';
      return isFeatured || (isCompleted && hasGallery) || hasGallery;
    })
    .sort((a, b) => {
      if (a.is_featured && !b.is_featured) return -1;
      if (!a.is_featured && b.is_featured) return 1;
      return new Date(b.date) - new Date(a.date);
    })
    .slice(0, 3);

  return (
    <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 text-white p-10 rounded-3xl">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <Star className="w-8 h-8 text-yellow-300 mr-3" />
          <h2 className="text-4xl font-bold">Event Highlights</h2>
        </div>
        <p className="text-xl text-green-100 max-w-3xl mx-auto">
          Celebrating the impact we've made together through our recent events and activities.
        </p>
      </div>
      
      {highlightEvents.length > 0 ? (
        <>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {highlightEvents.map((event) => (
              <HighlightEventCard key={event.id} event={event} />
            ))}
          </div>
          
          <div className="text-center">
            <button
              onClick={() => setActiveSection('events')}
              className="bg-white text-green-600 px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors flex items-center space-x-2 mx-auto font-semibold"
            >
              <Eye className="w-5 h-5" />
              <span>See All Event Galleries</span>
            </button>
          </div>
        </>
      ) : (
        <div className="text-center py-8 bg-white/10 rounded-2xl">
          <Star className="w-16 h-16 text-white/60 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">More Highlights Coming Soon</h3>
          <p className="text-green-100">
            We'll showcase our amazing events and photo galleries here as we continue our mission!
          </p>
        </div>
      )}
    </div>
  );
};

const UpcomingEventCard = ({ event }) => {
  const eventDate = new Date(event.date);
  const today = new Date();
  const daysUntil = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));

  return (
    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-100 hover:shadow-lg transition-all duration-300">
      <div className="flex justify-between items-start mb-4">
        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
          {event.category}
        </span>
        <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-semibold">
          {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days`}
        </span>
      </div>
      
      {event.is_featured && (
        <div className="mb-3">
          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-semibold flex items-center space-x-1 w-fit">
            <Star className="w-3 h-3" />
            <span>Featured</span>
          </span>
        </div>
      )}
      
      <h3 className="text-xl font-bold text-green-800 mb-3">{event.title}</h3>
      
      <div className="space-y-2 text-sm text-gray-600 mb-4">
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-green-600" />
          <span>{formatDate(event.date)}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Clock className="w-4 h-4 text-green-600" />
          <span>{event.time}</span>
        </div>
        <div className="flex items-center space-x-2">
          <MapPin className="w-4 h-4 text-green-600" />
          <span className="truncate">{event.location}</span>
        </div>
      </div>
      
      <p className="text-gray-700 text-sm line-clamp-3 mb-4">
        {event.description}
      </p>
      
      {event.registration_required && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
          <p className="text-yellow-800 text-xs font-medium">Registration Required</p>
        </div>
      )}
    </div>
  );
};

const HighlightEventCard = ({ event }) => (
  <div className="bg-white/10 backdrop-blur-sm p-4 sm:p-6 rounded-2xl hover:bg-white/20 transition-all duration-300">
    {/* Fixed image container */}
    <div className="h-32 sm:h-40 bg-white/20 rounded-lg mb-4 overflow-hidden relative">
      {event.gallery_images && event.gallery_images.length > 0 ? (
        <img 
          src={event.gallery_images[0]} 
          alt={event.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : event.image ? (
        <img 
          src={event.image} 
          alt={event.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon className="w-8 sm:w-12 h-8 sm:h-12 text-white/60" />
        </div>
      )}
    </div>
    
    <div className="flex flex-col sm:flex-row justify-between items-start mb-3 gap-2">
      <span className="bg-white/20 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
        {event.category}
      </span>
      {event.is_featured && (
        <Star className="w-4 sm:w-5 h-4 sm:h-5 text-yellow-300 fill-current flex-shrink-0" />
      )}
    </div>
    
    <h3 className="text-base sm:text-lg font-bold mb-2 line-clamp-2">{event.title}</h3>
    
    <div className="space-y-1 text-xs sm:text-sm text-green-100 mb-3">
      <div className="flex items-center space-x-2">
        <Calendar className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0" />
        <span className="truncate">{formatDate(event.date)}</span>
      </div>
      <div className="flex items-center space-x-2">
        <MapPin className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0" />
        <span className="truncate">{event.location}</span>
      </div>
    </div>
    
    {event.gallery_images && event.gallery_images.length > 1 && (
      <div className="flex items-center space-x-2 text-xs sm:text-sm text-green-100">
        <ImageIcon className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0" />
        <span>{event.gallery_images.length} photos available</span>
      </div>
    )}
  </div>
);


// Updated HeroSection with Globe GIF Background
const HeroSection = ({ setActiveSection, setShowContactForm, stats }) => (
  <div className="relative text-center py-20 bg-gradient-to-br from-green-100 via-emerald-100 to-teal-100 rounded-3xl overflow-hidden min-h-[80vh] flex items-center justify-center">
    {/* Optional: The previous colored overlay, can be removed if GIF is sufficient */}
    {/* <div className="absolute inset-0 bg-gradient-to-r from-green-600/10 to-emerald-600/10"></div> */}

    {/* ========================================================= */}
    {/* NEW: Background Globe GIF with Opacity */}
    {/* ========================================================= */}
    <img 
      src="/globe.gif" // Change to the actual filename of your GIF in public folder
      alt="Globe Animation"
      // Position it absolutely, cover the background, and set low opacity (10-15%)
      className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none"
    />
    {/* ========================================================= */}

    {/* Content Div - Z-10 keeps this above the GIF */}
    <div className="relative z-10 flex flex-col items-center px-4 w-full">
      
      {/* Logo and Title - Fixed for mobile */}
      <div className="flex flex-col sm:flex-row items-center justify-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-full flex items-center justify-center shadow-lg mb-4 sm:mb-0 sm:mr-4 flex-shrink-0">
          <img
            src="/logo.jpg"
            alt="Sahayaa Trust Logo"
            className="w-14 h-14 rounded-full object-cover"
          />
        </div>
        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-green-800 text-center">
          Sahayaa Trust
        </h1>
      </div>
      
      {/* Subtitle */}
      <p className="text-lg sm:text-xl lg:text-2xl text-green-700 max-w-3xl mx-auto mb-8 leading-relaxed text-center px-4">
        "Sahayaa" means <em>"the one who stands with you"</em><br />
        Building a compassionate society where everyone lives with dignity, health, knowledge, and joy.
      </p>
      
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4 mb-12 w-full max-w-4xl px-4">
        <ActionButton onClick={() => setActiveSection('about')} primary>
          Meet Our Team
        </ActionButton>
        <ActionButton onClick={() => setActiveSection('donations')} variant="emerald">
          Support Us
        </ActionButton>
        <ActionButton onClick={() => setActiveSection('events')}>
          Upcoming Events
        </ActionButton>
      </div>
      
      <StatsGrid stats={stats} />
    </div>
  </div>
);


const ActionButton = ({ children, onClick, primary, variant = 'green' }) => {
  const baseClasses = "w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full transition-all transform hover:scale-105 shadow-lg text-center";
  const variants = {
    green: primary 
      ? "bg-green-600 text-white hover:bg-green-700" 
      : "border-2 border-green-600 text-green-600 hover:bg-green-50",
    emerald: "bg-emerald-600 text-white hover:bg-emerald-700",
    blue: "bg-blue-600 text-white hover:bg-blue-700"
  };
  
  return (
    <button 
      onClick={onClick}
      className={`${baseClasses} ${variants[variant]}`}
    >
      {children}
    </button>
  );
};


// Updated StatsGrid with dynamic counts
const StatsGrid = ({ stats }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
    <StatCard value={`${stats.peopleHelped.toLocaleString()}+`} label="People Helped" />
    <StatCard value={`${stats.eventsCompleted}`} label="Events Completed" />
    <StatCard value={`${stats.volunteers}`} label="Volunteers" />
    <StatCard value={`${stats.members}`} label="Donors" />
  </div>
);

const StatCard = ({ value, label }) => (
  <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl">
    <div className="text-3xl font-bold text-green-800">{value}</div>
    <div className="text-green-600">{label}</div>
  </div>
);

const VisionMission = () => (
  <div className="grid lg:grid-cols-2 gap-12">
    <div className="bg-gradient-to-br from-green-600 to-emerald-600 text-white p-10 rounded-3xl">
      <div className="flex items-center mb-6">
        <Target className="w-8 h-8 mr-3" />
        <h2 className="text-3xl font-bold">Our Vision</h2>
      </div>
      <p className="text-lg leading-relaxed mb-6">
        To build a compassionate and empowered society where everyone—especially children and women—has the opportunity to live with dignity, health, knowledge, and joy.
      </p>
      <p className="text-green-100">
        We believe that care, when extended with sincerity and respect, can change the course of lives and communities.
      </p>
    </div>
    
    <div className="bg-white p-10 rounded-3xl shadow-xl border border-green-100">
      <div className="flex items-center mb-6">
        <Activity className="w-8 h-8 mr-3 text-green-600" />
        <h2 className="text-3xl font-bold text-green-800">Our Mission</h2>
      </div>
      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        Sahayaa Trust supports individuals and families in need, with a special focus on the well-being of children and women.
      </p>
      <p className="text-gray-600">
        We work across key areas that ensure a strong foundation for life, creating lasting positive impact in communities.
      </p>
    </div>
  </div>
);

const SahayaaValues = () => (
  <div className="bg-white p-10 rounded-3xl shadow-xl">
    <div className="text-center mb-12">
      <h2 className="text-4xl font-bold text-green-800 mb-4">What SAHAYAA Stands For</h2>
      <p className="text-xl text-gray-600 max-w-3xl mx-auto">
        Each letter in our name represents a core pillar of our work and commitment to the community.
      </p>
    </div>
    
    <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8">
      {SAHAYAA_VALUES.map((value, index) => {
        const IconComponent = value.icon;
        return (
          <ValueCard key={index} value={value} IconComponent={IconComponent} />
        );
      })}
    </div>
  </div>
);

const ValueCard = ({ value, IconComponent }) => (
  <div className="group cursor-pointer">
    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl hover:shadow-lg transition-all duration-300 h-full">
      <div className="flex items-center mb-4">
        <div className="w-16 h-16 bg-green-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mr-4 group-hover:bg-green-700 transition-colors">
          {value.letter}
        </div>
        <IconComponent className="w-8 h-8 text-green-600" />
      </div>
      <h3 className="text-xl font-bold text-green-800 mb-2">{value.word}</h3>
      <p className="text-green-600 font-medium mb-3">{value.meaning}</p>
      <p className="text-gray-600 text-sm mb-4">{value.description}</p>
      <p className="text-xs text-gray-500 leading-relaxed">{value.details}</p>
    </div>
  </div>
);

const OurIdeology = () => (
  <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 text-white p-10 rounded-3xl">
    <div className="text-center mb-8">
      <h2 className="text-3xl font-bold mb-4">Our Ideology</h2>
      <p className="text-xl text-green-100 max-w-3xl mx-auto">
        We believe in <strong>seva</strong>, not as charity, but as a shared journey.
      </p>
    </div>
    
    <div className="grid md:grid-cols-3 gap-8 text-center">
      <IdeologyCard 
        icon={Users}
        title="Everyone Has Potential"
        description="With the right support, anyone can rise and achieve their dreams."
      />
      <IdeologyCard 
        icon={Heart}
        title="Standing Together"
        description="Sahayaa stands with people—not above them—through life's challenges."
      />
      <IdeologyCard 
        icon={Globe}
        title="Shared Journey"
        description="Together we walk towards a more compassionate and just society."
      />
    </div>
  </div>
);

const IdeologyCard = ({ icon: Icon, title, description }) => (
  <div className="bg-white/10 p-6 rounded-2xl backdrop-blur-sm">
    <Icon className="w-12 h-12 mx-auto mb-4 text-green-200" />
    <h3 className="text-lg font-bold mb-2">{title}</h3>
    <p className="text-green-100 text-sm">{description}</p>
  </div>
);

const CoreValues = () => {
  const iconMap = {
    Heart,
    Users,
    Target,
    Globe,
    CheckCircle,
    TrendingUp
  };

  return (
    <div className="bg-white p-10 rounded-3xl shadow-xl">
      <h2 className="text-3xl font-bold text-green-800 mb-8 text-center">Our Core Values</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {CORE_VALUES.map((value, index) => {
          const IconComponent = typeof value.icon === 'string' ? iconMap[value.icon] : value.icon;
          return (
            <CoreValueCard key={index} value={value} IconComponent={IconComponent} />
          );
        })}
      </div>
    </div>
  );
};

const CoreValueCard = ({ value, IconComponent }) => (
  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl hover:shadow-md transition-shadow">
    <IconComponent className="w-8 h-8 text-green-600 mb-3" />
    <h3 className="font-bold text-green-800 mb-2">{value.title}</h3>
    <p className="text-sm text-gray-600 leading-relaxed">{value.desc}</p>
  </div>
);

const TestimonialsSection = ({ testimonials }) => (
  <div className="bg-gray-50 p-10 rounded-3xl">
    <h2 className="text-3xl font-bold text-green-800 mb-8 text-center">What People Say</h2>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {testimonials.slice(0, 3).map((testimonial) => (
        <TestimonialCard key={testimonial.id} testimonial={testimonial} />
      ))}
    </div>
  </div>
);

const TestimonialCard = ({ testimonial }) => (
  <div className="bg-white p-6 rounded-2xl shadow-sm">
    <div className="flex items-center mb-4">
      {[...Array(5)].map((_, i) => (
        <Star 
          key={i} 
          className={`w-4 h-4 ${i < testimonial.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
        />
      ))}
    </div>
    <p className="text-gray-700 mb-4 italic">"{testimonial.message}"</p>
    <div className="flex items-center">
      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
        {testimonial.image ? (
          <img src={testimonial.image} alt={testimonial.name} className="w-full h-full rounded-full object-cover" />
        ) : (
          <User className="w-6 h-6 text-green-600" />
        )}
      </div>
      <div>
        <div className="font-semibold text-green-800">{testimonial.name}</div>
        <div className="text-sm text-gray-600">{testimonial.role}</div>
      </div>
    </div>
  </div>
);

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export default Home;
